
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Save, Upload, Eye, Plus, X, Image as ImageIcon, Trash2 } from 'lucide-react';
import { HeroContent } from '@/types/hero';
import { getAllHeroContent, saveHeroContent, uploadHeroImage, getHeroImages, deleteHeroImage } from '@/services/heroService';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';

const HeroManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [availableImages, setAvailableImages] = useState<{ name: string; url: string; size: number; created_at: string }[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [isImageBrowserOpen, setIsImageBrowserOpen] = useState(false);
  const { toast } = useToast();
  
  const [homeHero, setHomeHero] = useState<HeroContent>({
    id: 'home',
    title: 'Elegant Furniture for Modern Living',
    subtitle: 'Discover our curated collection of high-quality furniture that combines style, comfort, and craftsmanship.',
    primary_button_text: 'Shop Now',
    primary_button_link: '/products',
    secondary_button_text: 'Our Story',
    secondary_button_link: '/about',
    background_image: 'https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80',
    media_type: 'image',
    video_url: null,
    carousel_images: [],
    page: 'home',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  
  const [aboutHero, setAboutHero] = useState<HeroContent>({
    id: 'about',
    title: 'Our Story',
    subtitle: 'Discover the passion and craftsmanship behind every piece of furniture we create.',
    primary_button_text: '',
    primary_button_link: '',
    secondary_button_text: '',
    secondary_button_link: '',
    background_image: '',
    media_type: 'image',
    video_url: null,
    carousel_images: [],
    page: 'about',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  
  const [contactHero, setContactHero] = useState<HeroContent>({
    id: 'contact',
    title: 'Contact Us',
    subtitle: 'Have questions or need assistance? We\'re here to help.',
    primary_button_text: '',
    primary_button_link: '',
    secondary_button_text: '',
    secondary_button_link: '',
    background_image: '',
    media_type: 'image',
    video_url: null,
    carousel_images: [],
    page: 'contact',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });
  
  useEffect(() => {
    const fetchHeroContent = async () => {
      setIsLoading(true);
      
      try {
        // Fetch hero content from the database
        const data = await getAllHeroContent();
        
        if (data && data.length > 0) {
          data.forEach((item: HeroContent) => {
            if (item.page === 'home') {
              setHomeHero(item);
            } else if (item.page === 'about') {
              setAboutHero(item);
            } else if (item.page === 'contact') {
              setContactHero(item);
            }
          });
        }
      } catch (error) {
        console.error('Error fetching hero content:', error);
        toast({
          title: 'Error',
          description: 'Failed to load hero content. Please try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchHeroContent();
  }, [toast]);
  
  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      const activeContent = 
        activeTab === 'home' ? homeHero : 
        activeTab === 'about' ? aboutHero : contactHero;
      
      // Save the content
      const savedContent = await saveHeroContent(activeContent);
      
      // Update the local state with the saved content
      if (activeTab === 'home') {
        setHomeHero(savedContent);
      } else if (activeTab === 'about') {
        setAboutHero(savedContent);
      } else {
        setContactHero(savedContent);
      }
      
      toast({
        title: 'Success',
        description: 'Hero content has been saved successfully.',
      });
    } catch (error) {
      console.error('Error saving hero content:', error);
      toast({
        title: 'Error',
        description: 'Failed to save hero content. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      // Upload the image
      const publicUrl = await uploadHeroImage(file);
      
      // Update the corresponding hero content
      if (activeTab === 'home') {
        setHomeHero({ ...homeHero, background_image: publicUrl });
      } else if (activeTab === 'about') {
        setAboutHero({ ...aboutHero, background_image: publicUrl });
      } else {
        setContactHero({ ...contactHero, background_image: publicUrl });
      }
      
      toast({
        title: 'Success',
        description: 'Image uploaded successfully.',
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleCarouselImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      // Upload the image
      const publicUrl = await uploadHeroImage(file);
      
      // Add to carousel images
      const currentImages = activeContent.carousel_images || [];
      const updatedImages = [...currentImages, publicUrl];
      
      // Update the corresponding hero content
      if (activeTab === 'home') {
        setHomeHero({ ...homeHero, carousel_images: updatedImages });
      } else if (activeTab === 'about') {
        setAboutHero({ ...aboutHero, carousel_images: updatedImages });
      } else {
        setContactHero({ ...contactHero, carousel_images: updatedImages });
      }
      
      toast({
        title: 'Success',
        description: 'Image added to carousel successfully.',
      });
    } catch (error) {
      console.error('Error uploading carousel image:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const removeCarouselImage = (index: number) => {
    const currentImages = activeContent.carousel_images || [];
    const updatedImages = currentImages.filter((_, i) => i !== index);
    
    // Update the corresponding hero content
    if (activeTab === 'home') {
      setHomeHero({ ...homeHero, carousel_images: updatedImages });
    } else if (activeTab === 'about') {
      setAboutHero({ ...aboutHero, carousel_images: updatedImages });
    } else {
      setContactHero({ ...contactHero, carousel_images: updatedImages });
    }
  };

  const loadAvailableImages = async () => {
    console.log('Loading available images...');
    setIsLoadingImages(true);
    try {
      const images = await getHeroImages();
      console.log('Loaded images:', images);
      setAvailableImages(images);
    } catch (error) {
      console.error('Error loading images:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available images.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingImages(false);
    }
  };

  const selectImage = (imageUrl: string) => {
    if (activeContent.media_type === 'carousel') {
      // Add to carousel images
      const currentImages = activeContent.carousel_images || [];
      const updatedImages = [...currentImages, imageUrl];
      updateActiveContent('carousel_images', updatedImages);
    } else {
      // Set as background image
      updateActiveContent('background_image', imageUrl);
    }
  };

  const handleDeleteImage = async (fileName: string) => {
    try {
      await deleteHeroImage(fileName);
      await loadAvailableImages(); // Refresh the list
      toast({
        title: 'Success',
        description: 'Image deleted successfully.',
      });
    } catch (error) {
      console.error('Error deleting image:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete image.',
        variant: 'destructive',
      });
    }
  };
  
  // Get the active hero content based on the active tab
  const activeContent = 
    activeTab === 'home' ? homeHero : 
    activeTab === 'about' ? aboutHero : contactHero;
  
  // Function to handle updating the active hero content
  const updateActiveContent = (field: keyof HeroContent, value: string | string[]) => {
    if (activeTab === 'home') {
      setHomeHero({ ...homeHero, [field]: value });
    } else if (activeTab === 'about') {
      setAboutHero({ ...aboutHero, [field]: value });
    } else {
      setContactHero({ ...contactHero, [field]: value });
    }
  };

  // Carousel component for preview
  const CarouselPreview: React.FC<{ images: string[] }> = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
      if (images.length > 1) {
        const interval = setInterval(() => {
          setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, 3000);
        return () => clearInterval(interval);
      }
    }, [images.length]);

    if (images.length === 0) return null;

    return (
      <div className="relative h-full w-full">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
        ))}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
            {images.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-white' : 'bg-white/50'
                }`}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Image Browser component
  const ImageBrowser: React.FC = () => {
    const handleOpenDialog = async () => {
      console.log('Opening image browser dialog...');
      setIsImageBrowserOpen(true);
      await loadAvailableImages();
    };

    return (
      <Dialog open={isImageBrowserOpen} onOpenChange={setIsImageBrowserOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" onClick={handleOpenDialog}>
            <ImageIcon size={16} className="mr-2" />
            Browse Existing Images
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select from Existing Images</DialogTitle>
            <DialogDescription>
              Choose an image from your existing collection to use as background or add to carousel.
            </DialogDescription>
          </DialogHeader>
          
          {isLoadingImages ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-2"></div>
                <p>Loading images...</p>
              </div>
            </div>
          ) : availableImages.length === 0 ? (
            <div className="text-center py-8">
              <ImageIcon size={48} className="mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">No images found in storage.</p>
              <p className="text-sm text-gray-400 mt-2">Upload some images first to see them here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {availableImages.map((image) => (
                <div key={image.name} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden border-2 border-transparent group-hover:border-blue-500 transition-colors">
                    <img
                      src={image.url}
                      alt={image.name}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => {
                        selectImage(image.url);
                        setIsImageBrowserOpen(false);
                      }}
                    />
                  </div>
                  
                  {/* Image info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="truncate">{image.name}</p>
                    <p>{(image.size / 1024 / 1024).toFixed(1)} MB</p>
                  </div>
                  
                  {/* Delete button */}
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage(image.name);
                    }}
                  >
                    <Trash2 size={14} />
                  </Button>
                  
                  {/* Select indicator */}
                  <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectImage(image.url);
                        setIsImageBrowserOpen(false);
                      }}
                    >
                      Select
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  };
  
  return (
    <div className="space-y-6">
      <Helmet>
        <title>Hero Management | Admin Dashboard</title>
      </Helmet>
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-sans font-semibold">Hero Section Management</h1>
        
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setPreviewMode(!previewMode)}
            variant="outline"
          >
            <Eye size={16} className="mr-2" />
            {previewMode ? 'Exit Preview' : 'Preview'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save size={16} className="mr-2" />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="home">Home Page</TabsTrigger>
          <TabsTrigger value="about">About Page</TabsTrigger>
          <TabsTrigger value="contact">Contact Page</TabsTrigger>
        </TabsList>
        
        <TabsContent value={activeTab} className="space-y-6">
          {previewMode ? (
            <div className="border rounded-lg overflow-hidden">
              <div className="relative h-80 flex items-center">
                {/* Background Media */}
                {activeContent.media_type === 'video' && activeContent.video_url ? (
                  <video
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                  >
                    <source src={activeContent.video_url} type="video/mp4" />
                  </video>
                ) : activeContent.media_type === 'carousel' && activeContent.carousel_images && activeContent.carousel_images.length > 0 ? (
                  <CarouselPreview images={activeContent.carousel_images} />
                ) : (
                  <div 
                    className="absolute inset-0 w-full h-full"
                    style={{ 
                      backgroundImage: `url(${activeContent.background_image || 'https://via.placeholder.com/1920x1080'})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  />
                )}
                
                <div className="absolute inset-0 bg-black/50"></div>
                <div className="relative z-10 container-custom text-white">
                  <h1 className="text-4xl font-sans mb-4">{activeContent.title}</h1>
                  <p className="text-lg mb-6 max-w-xl">{activeContent.subtitle}</p>
                  
                  {activeContent.primary_button_text && (
                    <div className="flex gap-4">
                      <button className="bg-white text-gray-900 px-6 py-2 rounded">
                        {activeContent.primary_button_text}
                      </button>
                      
                      {activeContent.secondary_button_text && (
                        <button className="border border-white px-6 py-2 rounded">
                          {activeContent.secondary_button_text}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Hero Content</h3>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title</Label>
                      <Input 
                        id="title" 
                        value={activeContent.title}
                        onChange={(e) => updateActiveContent('title', e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="subtitle">Subtitle</Label>
                      <Textarea 
                        id="subtitle" 
                        rows={3}
                        value={activeContent.subtitle}
                        onChange={(e) => updateActiveContent('subtitle', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Media Type</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="media_type">Select Media Type</Label>
                    <Select 
                      value={activeContent.media_type || 'image'} 
                      onValueChange={(value) => updateActiveContent('media_type', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select media type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="image">Single Image</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="carousel">Image Carousel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Buttons</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="primary_button_text">Primary Button Text</Label>
                        <Input 
                          id="primary_button_text" 
                          value={activeContent.primary_button_text || ''}
                          onChange={(e) => updateActiveContent('primary_button_text', e.target.value)}
                          placeholder="e.g., Shop Now"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="primary_button_link">Primary Button Link</Label>
                        <Input 
                          id="primary_button_link" 
                          value={activeContent.primary_button_link || ''}
                          onChange={(e) => updateActiveContent('primary_button_link', e.target.value)}
                          placeholder="e.g., /products"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="secondary_button_text">Secondary Button Text</Label>
                        <Input 
                          id="secondary_button_text" 
                          value={activeContent.secondary_button_text || ''}
                          onChange={(e) => updateActiveContent('secondary_button_text', e.target.value)}
                          placeholder="e.g., Learn More"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="secondary_button_link">Secondary Button Link</Label>
                        <Input 
                          id="secondary_button_link" 
                          value={activeContent.secondary_button_link || ''}
                          onChange={(e) => updateActiveContent('secondary_button_link', e.target.value)}
                          placeholder="e.g., /about"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Media Content based on type */}
                {activeContent.media_type === 'image' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Background Image</h3>
                    
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="background_image">Image URL</Label>
                        <Input 
                          id="background_image" 
                          value={activeContent.background_image || ''}
                          onChange={(e) => updateActiveContent('background_image', e.target.value)}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Or Upload a New Image</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            onChange={handleImageUpload}
                            accept="image/*"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Or Select from Existing Images</Label>
                        <ImageBrowser />
                      </div>
                      
                      {activeContent.background_image && (
                        <div className="mt-4">
                          <p className="text-sm text-muted-foreground mb-2">Current Background Image:</p>
                          <img 
                            src={activeContent.background_image} 
                            alt="Background Preview" 
                            className="max-h-40 object-cover rounded-md border"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeContent.media_type === 'video' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Background Video</h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="video_url">Video URL</Label>
                      <Input 
                        id="video_url" 
                        value={activeContent.video_url || ''}
                        onChange={(e) => updateActiveContent('video_url', e.target.value)}
                        placeholder="https://example.com/video.mp4"
                      />
                      <p className="text-sm text-muted-foreground">
                        Supported formats: MP4, WebM, OGG. Video will autoplay, loop, and be muted.
                      </p>
                    </div>
                  </div>
                )}

                {activeContent.media_type === 'carousel' && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Image Carousel</h3>
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Add Images to Carousel</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            onChange={handleCarouselImageUpload}
                            accept="image/*"
                            className="flex-1"
                          />
                          <Button type="button" variant="outline" size="sm">
                            <Plus size={16} className="mr-2" />
                            Add Image
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Images will automatically slide every 3 seconds. Add multiple images to create a carousel.
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Or Select from Existing Images</Label>
                        <ImageBrowser />
                      </div>
                      
                      {activeContent.carousel_images && activeContent.carousel_images.length > 0 && (
                        <div className="space-y-2">
                          <Label>Carousel Images ({activeContent.carousel_images.length})</Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {activeContent.carousel_images.map((image, index) => (
                              <div key={index} className="relative group">
                                <img 
                                  src={image} 
                                  alt={`Carousel image ${index + 1}`} 
                                  className="w-full h-32 object-cover rounded-md border"
                                />
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={() => removeCarouselImage(index)}
                                >
                                  <X size={14} />
                                </Button>
                                <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                  {index + 1}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HeroManagement;
